/**
 * Profile avatars in the Storybook style (ids match core AVATARS: fox, cat, bear, bunny, mouse, dog,
 * frog, kid): head-and-shoulders portraits on a soft coloured disc, built with the same rules as the
 * lessons (big head, outer/inner line, one shade crescent, two eye highlights).
 */
import { rolePalette } from '@sketchling/core';
import type { FigureDef, Layer } from '../Figure';
import { blob, ellipse, mirrorX, poly } from '../geom';

export const AVATAR_IDS = ['fox', 'cat', 'bear', 'bunny', 'mouse', 'dog', 'frog', 'kid'] as const;
export type AvatarId = (typeof AVATAR_IDS)[number];

export const AVATAR_PALETTE = rolePalette({
  discFox: '#F6D9C2',
  discCat: '#F7E3B5',
  discBear: '#E6D6C6',
  discBunny: '#F6D6DE',
  discMouse: '#DCDDF0',
  discDog: '#CFE6F0',
  discFrog: '#D7ECCB',
  discKid: '#F3DCCB',
  bearFur: '#B07A52',
  bearMuzzle: '#E7C9A6',
  bunnyFur: '#F4ECE6',
  mouseFur: '#A9A3B8',
  mouseEar: '#F2B6C0',
  dogFur: '#D9A066',
  dogEar: '#8A5A3C',
  frogSkin: '#8CC66B',
  frogBelly: '#D9EBB0',
  shirt: '#5AAFD9',
});

const M = (d: string) => mirrorX(d, 120);
const eyes = (y: number, dx = 26, rx = 9, ry = 11): Layer[] =>
  [120 - dx, 120 + dx].flatMap((cx): Layer[] => [
    { k: 'spot', d: ellipse(cx, y, rx, ry), fill: 'eye' },
    { k: 'spot', d: ellipse(cx + rx * 0.32, y - ry * 0.38, rx * 0.4, rx * 0.4), fill: 'white' },
    { k: 'spot', d: ellipse(cx - rx * 0.34, y + ry * 0.42, rx * 0.17, rx * 0.17), fill: 'white' },
  ]);
const blush = (y: number, dx = 44): Layer[] => [
  { k: 'spot', d: ellipse(120 - dx, y, 10, 6), fill: 'blush', blush: true, opacity: 0.6 },
  { k: 'spot', d: ellipse(120 + dx, y, 10, 6), fill: 'blush', blush: true, opacity: 0.6 },
];
const smile = (y: number): Layer[] => [
  { k: 'ink', d: `M120 ${y}C120 ${y + 6} 115 ${y + 9} 110 ${y + 7}`, w: 0.85 },
  { k: 'ink', d: M(`M120 ${y}C120 ${y + 6} 115 ${y + 9} 110 ${y + 7}`), w: 0.85 },
];
const nose = (y: number, fill = 'pink', w = 9): Layer => ({ k: 'region', d: `M${120 - w} ${y - 4}C${120 - w} ${y - 9} ${120 + w} ${y - 9} ${120 + w} ${y - 4}C${120 + w} ${y + 1} ${124} ${y + 4} 120 ${y + 4}C116 ${y + 4} ${120 - w} ${y + 1} ${120 - w} ${y - 4}Z`, fill, shade: false });
const body = (fill: string): Layer => ({ k: 'region', d: 'M50 240C52 206 80 186 120 186C160 186 188 206 190 240Z', fill, sil: true, shade: 10, rim: true });
const head = (fill: string, d = 'M120 52C162 52 190 78 190 118C190 156 160 180 120 180C80 180 50 156 50 118C50 78 78 52 120 52Z'): Layer => ({ k: 'region', d, fill, sil: true, shade: 12, rim: true });

function build(id: AvatarId): Layer[] {
  switch (id) {
    case 'fox': {
      const ear = 'M60 106C52 80 54 52 64 32C67 26 74 26 78 30C90 44 100 58 106 70Z';
      return [
        body('scarf'),
        { k: 'region', d: ear, fill: 'foxFur', sil: true, shade: 6 },
        { k: 'region', d: M(ear), fill: 'foxFur', sil: true, shade: 6 },
        { k: 'region', d: 'M70 80C68 64 70 50 74 42C82 50 90 60 95 68Z', fill: 'foxDark', line: false, shade: false },
        { k: 'region', d: M('M70 80C68 64 70 50 74 42C82 50 90 60 95 68Z'), fill: 'foxDark', line: false, shade: false },
        head('foxFur', 'M120 62C152 62 172 78 180 100C186 114 194 126 202 136C186 142 172 152 158 162C146 170 134 174 120 174C106 174 94 170 82 162C68 152 54 142 38 136C46 126 54 114 60 100C68 78 88 62 120 62Z'),
        { k: 'region', d: 'M38 136C54 142 68 152 82 162C94 170 106 174 120 174C134 174 146 170 158 162C172 152 186 142 202 136C190 128 174 122 158 124C142 126 130 132 120 144C110 132 98 126 82 124C66 122 50 128 38 136Z', fill: 'cream', shade: 5 },
        ...eyes(116, 24, 8.5, 10.5),
        ...blush(134, 42),
        nose(144, 'foxDark', 8),
        ...smile(148),
      ];
    }
    case 'cat': {
      const ear = 'M62 104C56 80 58 52 66 34C69 28 76 27 80 31C90 42 100 54 106 66Z';
      return [
        body('collar'),
        { k: 'region', d: ear, fill: 'catFur', sil: true, shade: 6 },
        { k: 'region', d: M(ear), fill: 'catFur', sil: true, shade: 6 },
        { k: 'region', d: 'M72 82C70 68 72 56 76 46C83 53 90 61 95 69Z', fill: 'pink', line: false, shade: false },
        { k: 'region', d: M('M72 82C70 68 72 56 76 46C83 53 90 61 95 69Z'), fill: 'pink', line: false, shade: false },
        head('catFur'),
        { k: 'ink', d: 'M110 58C111 64 112 69 113 74', w: 1.4, color: 'catStripe' },
        { k: 'ink', d: 'M120 55C120 62 120 68 120 76', w: 1.5, color: 'catStripe' },
        { k: 'ink', d: 'M130 58C129 64 128 69 127 74', w: 1.4, color: 'catStripe' },
        { k: 'region', d: 'M96 142C96 130 108 126 120 132C132 126 144 130 144 142C144 154 132 158 120 156C108 158 96 154 96 142Z', fill: 'cream', line: false, shade: false },
        ...eyes(118),
        ...blush(140),
        nose(136),
        ...smile(140),
        { k: 'ink', d: 'M82 142C70 139 60 139 48 141', w: 0.55, taper: 'end' },
        { k: 'ink', d: M('M82 142C70 139 60 139 48 141'), w: 0.55, taper: 'end' },
      ];
    }
    case 'bear':
      return [
        body('sweater'),
        { k: 'region', d: ellipse(70, 72, 22), fill: 'bearFur', sil: true, shade: 5 },
        { k: 'region', d: ellipse(170, 72, 22), fill: 'bearFur', sil: true, shade: 5 },
        { k: 'region', d: ellipse(70, 72, 11), fill: 'bearMuzzle', line: false, shade: false },
        { k: 'region', d: ellipse(170, 72, 11), fill: 'bearMuzzle', line: false, shade: false },
        head('bearFur'),
        { k: 'region', d: ellipse(120, 146, 30, 22), fill: 'bearMuzzle', shade: 4 },
        ...eyes(116, 30, 8, 9.5),
        ...blush(140, 50),
        nose(138, 'eye', 9),
        ...smile(142),
      ];
    case 'bunny': {
      const ear = 'M88 92C78 66 74 34 82 14C88 2 102 4 106 18C112 38 110 66 106 90Z';
      return [
        body('overall'),
        { k: 'region', d: ear, fill: 'bunnyFur', sil: true, shade: 5 },
        { k: 'region', d: M(ear), fill: 'bunnyFur', sil: true, shade: 5 },
        { k: 'region', d: 'M92 80C86 60 86 36 90 24C94 18 99 20 100 28C102 44 101 64 99 80Z', fill: 'pink', line: false, shade: false },
        { k: 'region', d: M('M92 80C86 60 86 36 90 24C94 18 99 20 100 28C102 44 101 64 99 80Z'), fill: 'pink', line: false, shade: false },
        head('bunnyFur'),
        ...eyes(120),
        ...blush(142),
        nose(140, 'pink', 7),
        ...smile(142),
        { k: 'region', d: 'M114 150L126 150L126 160C126 163 114 163 114 160Z', fill: 'white', shade: false },
      ];
    }
    case 'mouse':
      return [
        body('pink'),
        { k: 'region', d: ellipse(64, 70, 34), fill: 'mouseFur', sil: true, shade: 6 },
        { k: 'region', d: ellipse(176, 70, 34), fill: 'mouseFur', sil: true, shade: 6 },
        { k: 'region', d: ellipse(66, 72, 21), fill: 'mouseEar', line: false, shade: false },
        { k: 'region', d: ellipse(174, 72, 21), fill: 'mouseEar', line: false, shade: false },
        head('mouseFur', 'M120 64C160 64 186 88 186 120C186 156 156 182 120 182C84 182 54 156 54 120C54 88 80 64 120 64Z'),
        ...eyes(122, 26, 8.5, 10.5),
        ...blush(144),
        nose(144, 'pink', 8),
        ...smile(148),
        { k: 'ink', d: 'M86 150C74 148 64 148 52 150', w: 0.5, taper: 'end' },
        { k: 'ink', d: M('M86 150C74 148 64 148 52 150'), w: 0.5, taper: 'end' },
      ];
    case 'dog':
      return [
        body('collar'),
        head('dogFur'),
        { k: 'region', d: 'M62 74C42 82 34 118 44 150C50 166 64 164 70 150C76 132 80 104 84 82C80 72 72 70 62 74Z', fill: 'dogEar', sil: true, shade: 5 },
        { k: 'region', d: M('M62 74C42 82 34 118 44 150C50 166 64 164 70 150C76 132 80 104 84 82C80 72 72 70 62 74Z'), fill: 'dogEar', sil: true, shade: 5 },
        { k: 'region', d: ellipse(142, 110, 22, 18), fill: 'cream', line: false, shade: false },
        { k: 'region', d: ellipse(120, 150, 32, 22), fill: 'cream', shade: 4 },
        ...eyes(116, 24, 8.5, 10),
        ...blush(140, 48),
        nose(140, 'eye', 10),
        ...smile(144),
        { k: 'region', d: 'M112 154C112 168 128 168 128 154Z', fill: 'tongue', shade: false },
      ];
    case 'frog':
      return [
        body('frogSkin'),
        { k: 'region', d: ellipse(82, 68, 28), fill: 'frogSkin', sil: true, shade: 5 },
        { k: 'region', d: ellipse(158, 68, 28), fill: 'frogSkin', sil: true, shade: 5 },
        head('frogSkin', 'M120 70C166 70 196 96 196 128C196 160 162 180 120 180C78 180 44 160 44 128C44 96 74 70 120 70Z'),
        { k: 'region', d: ellipse(82, 68, 18), fill: 'white', shade: false },
        { k: 'region', d: ellipse(158, 68, 18), fill: 'white', shade: false },
        { k: 'spot', d: ellipse(84, 70, 9, 10), fill: 'eye' },
        { k: 'spot', d: ellipse(87, 66, 3.4), fill: 'white' },
        { k: 'spot', d: ellipse(156, 70, 9, 10), fill: 'eye' },
        { k: 'spot', d: ellipse(159, 66, 3.4), fill: 'white' },
        ...blush(136, 52),
        { k: 'ink', d: 'M88 132C102 148 138 148 152 132', w: 1 },
        { k: 'spot', d: ellipse(110, 112, 2.4), fill: 'line' },
        { k: 'spot', d: ellipse(130, 112, 2.4), fill: 'line' },
      ];
    case 'kid':
      return [
        body('shirt'),
        { k: 'region', d: 'M54 128C48 76 80 46 120 46C160 46 192 76 186 128C184 150 176 166 166 176L74 176C64 166 56 150 54 128Z', fill: 'hair', sil: true, shade: 8 },
        { k: 'region', d: ellipse(62, 126, 9, 12), fill: 'skin', sil: true, shade: false },
        { k: 'region', d: ellipse(178, 126, 9, 12), fill: 'skin', sil: true, shade: false },
        head('skin', 'M120 66C150 66 172 88 172 120C172 154 150 178 120 178C90 178 68 154 68 120C68 88 90 66 120 66Z'),
        { k: 'region', d: blob([[66, 116], [74, 80], [104, 60], [140, 60], [168, 80], [174, 116], [154, 96], [128, 88], [104, 98], [84, 96]]), fill: 'hair', shade: 5 },
        ...eyes(128, 22, 7.5, 9),
        ...blush(146, 36),
        { k: 'ink', d: 'M118 134C116 140 117 143 121 143', w: 0.55 },
        { k: 'region', d: 'M106 152C110 164 130 164 134 152C126 155 114 155 106 152Z', fill: 'mouth', shade: false },
        { k: 'region', d: poly([[118, 46], [128, 30], [134, 48]]), fill: 'hair', sil: true, shade: false },
      ];
  }
}

const cache = new Map<AvatarId, FigureDef>();
export function avatarFigure(id: AvatarId): FigureDef {
  let d = cache.get(id);
  if (!d) {
    d = { vb: [240, 240], layers: build(id), guides: [] };
    cache.set(id, d);
  }
  return d;
}

export const AVATAR_DISC: Record<AvatarId, string> = {
  fox: 'discFox',
  cat: 'discCat',
  bear: 'discBear',
  bunny: 'discBunny',
  mouse: 'discMouse',
  dog: 'discDog',
  frog: 'discFrog',
  kid: 'discKid',
};
